import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiBookOpen, FiArrowRight } = FiIcons;

const BookTypeSelector = ({ onSelect }) => {
  const bookTypes = [
    {
      type: 'fiction',
      title: 'Christian Fiction',
      description: 'Novels, romance, thrillers, historical fiction, and contemporary stories',
      icon: FiUsers,
      color: 'from-blue-500 to-purple-600',
      features: ['Character development', 'Plot hooks', 'Emotional stakes', 'Theme integration']
    },
    {
      type: 'nonfiction',
      title: 'Christian Nonfiction',
      description: 'Devotionals, Bible studies, memoirs, self-help, and inspirational books',
      icon: FiBookOpen,
      color: 'from-green-500 to-blue-600',
      features: ['Problem-solution focus', 'Authority building', 'Practical benefits', 'Target audience']
    }
  ];

  return (
    <div className="max-w-6xl mx-auto">
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="text-3xl font-bold text-gray-800 mb-4">
          What type of book are you writing?
        </h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Choose your book category to get personalized prompts that will help you create 
          a compelling description that connects with your target readers.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-8">
        {bookTypes.map((book, index) => (
          <motion.div
            key={book.type}
            className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer group"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.2 }}
            whileHover={{ y: -5 }}
            onClick={() => onSelect(book.type)}
          >
            <div className={`h-2 bg-gradient-to-r ${book.color}`}></div>
            
            <div className="p-8">
              <div className="flex items-center space-x-4 mb-6">
                <div className={`p-4 rounded-full bg-gradient-to-r ${book.color} text-white`}>
                  <SafeIcon icon={book.icon} className="text-2xl" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">{book.title}</h3>
                  <p className="text-gray-600">{book.description}</p>
                </div>
              </div>

              <div className="space-y-3 mb-8">
                <h4 className="font-semibold text-gray-700 mb-3">This tool will help you with:</h4>
                {book.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-gray-600">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Click to get started</span>
                <div className="flex items-center space-x-2 text-purple-600 group-hover:text-purple-700">
                  <span className="font-medium">Choose This Type</span>
                  <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default BookTypeSelector;